import React, { useCallback, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import Select from "react-select"
import PageHeader from "../../../components/DashboardComponents/PageHeader"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import EmptyState from "../../../components/Common/EmptyState"
import {
  ChevronDropdown,
  customStyles,
} from "../../../components/OnboardingComponents/RoleSelect"
import { ICONS } from "../../../constants/icons"
import { get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { getApiFieldErrors } from "../../../helpers/formFieldErrors"
import {
  collectRoleTypeSelectOptions,
  FORM_CONFIG,
  mapKeywordMappingRow,
  mapRoleClassSelectOptions,
  mapSpellbookSelectOptions,
} from "./adminArtsGenomeData"

const FORM_ID = "admin-keyword-mapping-form"

const findOption = (options, value) =>
  options.find(option => option.value === value) || null

const FormSelect = ({
  id,
  label,
  required = false,
  optional = false,
  placeholder,
  options,
  value,
  onChange,
  error = "",
}) => {
  const hasError = Boolean(error)
  return (
  <div className={`amok-field ${hasError ? "amok-field--error" : ""}`.trim()}>
    <label className="amok-field__label" htmlFor={id}>
      {label}
      {required ? <span className="amok-field__required"> *</span> : null}
      {optional ? (
        <span className="admin-arts-genome-form__optional"> (Optional)</span>
      ) : null}
    </label>
    <Select
      inputId={id}
      options={options}
      value={findOption(options, value)}
      onChange={option => onChange(option?.value || "")}
      placeholder={placeholder}
      styles={customStyles(hasError)}
      classNamePrefix="amok-role-select"
      isSearchable
      isClearable={optional}
      components={{
        DropdownIndicator: ChevronDropdown,
        IndicatorSeparator: null,
      }}
    />
    {hasError ? (
      <p className="amok-field__message amok-field__message--error">{error}</p>
    ) : null}
  </div>
  )
}

FormSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  placeholder: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
}

/**
 * Create / Edit Keyword Mapping — POST /api/v1/admin/role-types
 */
const KeywordMappingForm = ({ mode = "create" }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { itemId } = useParams()
  const isEdit = mode === "edit"
  const formConfig = FORM_CONFIG.keyword

  const [roleInternal, setRoleInternal] = useState("")
  const [publicRole, setPublicRole] = useState("")
  const [spellbookId, setSpellbookId] = useState("")
  const [secondarySpellbookId, setSecondarySpellbookId] = useState("")
  const [roleClassId, setRoleClassId] = useState("")
  const [secondaryRoleClassId, setSecondaryRoleClassId] = useState("")
  const [spellbookOptions, setSpellbookOptions] = useState([])
  const [classOptions, setClassOptions] = useState([])
  const [saving, setSaving] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  const applyItemToForm = useCallback(item => {
    const mapped = mapKeywordMappingRow(item)
    setRoleInternal(mapped.roleInternal === "--" ? "" : mapped.roleInternal)
    setPublicRole(mapped.publicRole === "--" ? "" : mapped.publicRole)
    setSpellbookId(mapped.spellbookId || "")
    setSecondarySpellbookId(mapped.secondarySpellbookId || "")
    setRoleClassId(mapped.roleClassId || "")
    setSecondaryRoleClassId(mapped.secondaryRoleClassId || "")
  }, [])

  const bootstrap = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const [spellbooksResponse, classesResponse, listResponse] =
        await Promise.all([
          get(endpoints.getAdminSpellbooks, {
            params: { page: 1, page_size: 100 },
          }),
          get(endpoints.getAdminRoleClasses, {
            params: {
              page: 1,
              page_size: 100,
              sort_by: "name",
              sort_direction: "asc",
            },
          }),
          get(endpoints.getAdminRoleTypes, {
            params: {
              page: 1,
              page_size: 50,
              sort_by: "created_at",
              sort_direction: "desc",
            },
          }),
        ])

      let optionsSource = []
      if (listResponse?.data?.success) {
        optionsSource = listResponse.data.result?.data || []
      }

      const fallback = collectRoleTypeSelectOptions(optionsSource)

      let nextSpellbooks = []
      if (spellbooksResponse?.data?.success) {
        nextSpellbooks = mapSpellbookSelectOptions(
          spellbooksResponse.data.result?.data || [],
        )
      } else {
        toastApiError(spellbooksResponse, "Failed to load spellbook options")
        nextSpellbooks = fallback.spellbookOptions
      }

      let nextClasses = []
      if (classesResponse?.data?.success) {
        nextClasses = mapRoleClassSelectOptions(
          classesResponse.data.result?.data || [],
        )
      } else {
        toastApiError(classesResponse, "Failed to load class options")
        nextClasses = fallback.classOptions
      }

      setSpellbookOptions(nextSpellbooks)
      setClassOptions(nextClasses)

      if (!isEdit) {
        setRoleInternal("")
        setPublicRole("")
        setSpellbookId("")
        setSecondarySpellbookId("")
        setRoleClassId("")
        setSecondaryRoleClassId("")
        return
      }

      const fromNav = location.state?.keyword
      if (fromNav?.id && String(fromNav.id) === String(itemId)) {
        applyItemToForm(fromNav)
        const { spellbookOptions: mergedSb, classOptions: mergedCl } =
          collectRoleTypeSelectOptions([fromNav, ...optionsSource])
        if (nextSpellbooks.length === 0) setSpellbookOptions(mergedSb)
        if (nextClasses.length === 0) setClassOptions(mergedCl)
        return
      }

      const detailResponse = await get(endpoints.getAdminRoleTypeById(itemId))
      if (detailResponse?.data?.success) {
        const result = detailResponse.data.result || {}
        applyItemToForm(result)
        const { spellbookOptions: mergedSb, classOptions: mergedCl } =
          collectRoleTypeSelectOptions([result, ...optionsSource])
        if (nextSpellbooks.length === 0) setSpellbookOptions(mergedSb)
        if (nextClasses.length === 0) setClassOptions(mergedCl)
      } else {
        setLoadError(
          toastApiError(detailResponse, "Failed to load keyword mapping"),
        )
      }
    } catch (err) {
      setLoadError(toastApiError(err, "Failed to load keyword mapping"))
    } finally {
      setIsLoading(false)
    }
  }, [applyItemToForm, isEdit, itemId, location.state])

  useEffect(() => {
    document.title = `${
      isEdit ? formConfig.editTitle : formConfig.createTitle
    } | AMOK . EARTH Admin`
  }, [formConfig, isEdit])

  useEffect(() => {
    bootstrap()
  }, [bootstrap])

  const handleCancel = () => {
    navigate("/admin/arts-genome", { state: { tab: "keyword" } })
  }

  const handleSave = async event => {
    event.preventDefault()

    const nextErrors = {}
    if (!roleInternal.trim()) {
      nextErrors.roleInternal = "Role (Internal) is required."
    }
    if (!publicRole.trim()) {
      nextErrors.publicRole = "Public Role is required."
    }
    if (!spellbookId) {
      nextErrors.spellbookId = "Spellbook is required."
    }
    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors)
      return
    }
    setFieldErrors({})

    setSaving(true)
    try {
      const payload = {
        name: roleInternal.trim(),
        public_name: publicRole.trim(),
        spellbook_id: spellbookId,
      }
      if (roleClassId) payload.role_class_id = roleClassId
      if (secondarySpellbookId) {
        payload.secondary_spellbook_id = secondarySpellbookId
      }
      if (secondaryRoleClassId) {
        payload.secondary_role_class_id = secondaryRoleClassId
      }

      const response = isEdit
        ? await post(endpoints.updateAdminRoleType(itemId), payload, {
            skipErrorToast: true,
          })
        : await post(endpoints.createAdminRoleType, payload, {
            skipErrorToast: true,
          })

      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        const apiFieldErrors = getApiFieldErrors(response, {
          name: "roleInternal",
          public_name: "publicRole",
          spellbook_id: "spellbookId",
        })
        if (Object.keys(apiFieldErrors).length) {
          setFieldErrors(apiFieldErrors)
          return
        }
        toastApiError(
          response,
          isEdit
            ? "Failed to update keyword mapping"
            : "Failed to create keyword mapping",
        )
        return
      }

      toast.success(
        response?.data?.message ||
          (isEdit
            ? `Keyword mapping for “${roleInternal.trim()}” updated successfully.`
            : `Keyword mapping for “${roleInternal.trim()}” saved successfully.`),
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )
      navigate("/admin/arts-genome", { state: { tab: "keyword" } })
    } catch (err) {
      toastApiError(
        err,
        isEdit
          ? "Failed to update keyword mapping"
          : "Failed to create keyword mapping",
      )
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div
        className="admin-page admin-arts-genome-form admin-arts-genome-form--keyword"
        aria-busy="true"
        aria-label="Loading keyword mapping form"
      >
        <div className="admin-arts-genome-form__header admin-arts-genome-form__header--skeleton">
          <div className="admin-arts-genome-form__skeleton admin-arts-genome-form__skeleton--title" />
          <div className="admin-arts-genome-form__skeleton admin-arts-genome-form__skeleton--subtitle" />
        </div>
        <div className="admin-arts-genome-form__fields">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`field-skel-${index}`}
              className="admin-arts-genome-form__row"
            >
              <div className="admin-arts-genome-form__skeleton admin-arts-genome-form__skeleton--field" />
              <div className="admin-arts-genome-form__skeleton admin-arts-genome-form__skeleton--field" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (isEdit && loadError && !roleInternal) {
    return (
      <div className="admin-page admin-arts-genome-form">
        <EmptyState
          title="Unable to load keyword mapping"
          description={loadError}
          actionText="Try again"
          onActionClick={bootstrap}
        />
        <CustomButton
          variant="secondary"
          size="md"
          type="button"
          onClick={handleCancel}
        >
          Back to Arts Genome
        </CustomButton>
      </div>
    )
  }

  return (
    <div className="admin-page admin-arts-genome-form admin-arts-genome-form--keyword">
      <PageHeader
        className="admin-arts-genome-form__header"
        title={isEdit ? formConfig.editTitle : formConfig.createTitle}
        subtitle={
          isEdit ? formConfig.editSubtitle : formConfig.createSubtitle
        }
        actions={
          <>
            <CustomButton
              variant="secondary"
              size="md"
              type="button"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              size="md"
              type="submit"
              form={FORM_ID}
              loading={saving}
              disabled={saving}
              trailingIconSrc={ICONS.arrowRight12}
              trailingIconAlt=""
            >
              {formConfig.saveLabel}
            </CustomButton>
          </>
        }
      />

      <form
        id={FORM_ID}
        className="admin-arts-genome-form__form"
        onSubmit={handleSave}
      >
        <div className="admin-arts-genome-form__fields">
          <div className="admin-arts-genome-form__row">
            <CustomInput
              label="Role (Internal)"
              name="roleInternal"
              required
              placeholder="Enter role name"
              value={roleInternal}
              onChange={event => {
                setRoleInternal(event.target.value)
                if (fieldErrors.roleInternal) {
                  setFieldErrors(prev => ({ ...prev, roleInternal: "" }))
                }
              }}
              error={fieldErrors.roleInternal}
            />
            <CustomInput
              label="Public Role"
              name="publicRole"
              required
              placeholder="Enter public role"
              value={publicRole}
              onChange={event => {
                setPublicRole(event.target.value)
                if (fieldErrors.publicRole) {
                  setFieldErrors(prev => ({ ...prev, publicRole: "" }))
                }
              }}
              error={fieldErrors.publicRole}
            />
          </div>

          <div className="admin-arts-genome-form__row">
            <FormSelect
              id="keyword-spellbook"
              label="Select Spellbook"
              required
              placeholder="Select spellbook"
              options={spellbookOptions}
              value={spellbookId}
              onChange={next => {
                setSpellbookId(next)
                if (fieldErrors.spellbookId) {
                  setFieldErrors(prev => ({ ...prev, spellbookId: "" }))
                }
              }}
              error={fieldErrors.spellbookId}
            />
            <FormSelect
              id="keyword-spellbook-secondary"
              label="Select Secondary Spellbook"
              optional
              placeholder="Select secondary spellbook"
              options={spellbookOptions}
              value={secondarySpellbookId}
              onChange={setSecondarySpellbookId}
            />
          </div>

          <div className="admin-arts-genome-form__row">
            <FormSelect
              id="keyword-class"
              label="Select Class"
              optional
              placeholder="Select class"
              options={classOptions}
              value={roleClassId}
              onChange={setRoleClassId}
            />
            <FormSelect
              id="keyword-class-secondary"
              label="Select Secondary Class"
              optional
              placeholder="Select secondary class"
              options={classOptions}
              value={secondaryRoleClassId}
              onChange={setSecondaryRoleClassId}
            />
          </div>
        </div>
      </form>
    </div>
  )
}

KeywordMappingForm.propTypes = {
  mode: PropTypes.oneOf(["create", "edit"]),
}

KeywordMappingForm.defaultProps = {
  mode: "create",
}

export default KeywordMappingForm
